import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase/config';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';

// MUI components
import { 
  Container, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardActions, 
  Grid, 
  Box,
  Chip,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QrCodeIcon from '@mui/icons-material/QrCode';

const Home = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [hostedParties, setHostedParties] = useState([]);
  const [invitedParties, setInvitedParties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Fetch hosted parties
    const fetchHostedParties = async () => {
      const { data, error } = await supabase
        .from('parties')
        .select('*')
        .eq('host', user.id);
      
      if (!error && data) {
        setHostedParties(data);
      }
      setLoading(false);
    };

    // Fetch invited parties
    const fetchInvitedParties = async () => {
      const { data, error } = await supabase
        .from('parties')
        .select('*')
        .contains('invitees', [user.email]);
      
      if (!error && data) {
        setInvitedParties(data);
      }
    };

    // Initial fetch
    fetchHostedParties();
    fetchInvitedParties();

    // Set up real-time subscriptions
    const hostedSubscription = supabase
      .channel('hosted-parties-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'parties',
          filter: `host=eq.${user.id}`
        }, 
        () => {
          fetchHostedParties();
        }
      )
      .subscribe();

    const invitedSubscription = supabase
      .channel('invited-parties-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'parties'
        }, 
        () => {
          fetchInvitedParties();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(hostedSubscription);
      supabase.removeChannel(invitedSubscription);
    };
  }, [user]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh' 
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg">
        <Box 
          sx={{ 
            my: 8, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 4
          }}
        >
          <Box sx={{ maxWidth: 600, textAlign: { xs: 'center', md: 'left' } }}>
            <Typography 
              variant="h2" 
              component="h1" 
              fontWeight="bold"
              color="primary.main"
              gutterBottom
            >
              {t.landingTitle}
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ mb: 4 }}
            >
              {t.landingSubtitle}
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: { xs: 'center', md: 'flex-start' } 
              }}
            >
              <Button 
                component={Link} 
                to="/login" 
                variant="contained" 
                color="primary"
                size="large"
                sx={{ 
                  borderRadius: 8,
                  px: 4,
                  py: 1.5
                }}
              >
                {t.login}
              </Button>
              <Button 
                component={Link} 
                to="/signup" 
                variant="outlined" 
                color="primary"
                size="large"
                sx={{ 
                  borderRadius: 8,
                  px: 4,
                  py: 1.5
                }}
              >
                {t.getStarted}
              </Button>
            </Box>
          </Box>
          <img
            src="/party-image.jpg"
            alt="Party"
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
          />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: 4,
            gap: 2
          }}
        >
          <Typography variant="h4" component="h1" fontWeight="bold">
            {t.home}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              component={Link} 
              to="/join" 
              variant="outlined" 
              color="primary"
              startIcon={<QrCodeIcon />}
              sx={{ borderRadius: 8 }}
            >
              {t.joinParty}
            </Button>
            <Button 
              component={Link} 
              to="/create-party" 
              variant="contained" 
              color="primary"
              startIcon={<AddIcon />}
              sx={{ borderRadius: 8 }}
            >
              {t.createParty}
            </Button>
          </Box>
        </Box>

        {/* Hosted Parties */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography 
            variant="h5" 
            component="h2" 
            fontWeight="bold" 
            color="primary.main" 
            gutterBottom
          >
            {t.yourParties}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            {hostedParties.length > 0 ? (
              hostedParties.map(party => (
                <Grid item xs={12} sm={6} md={4} key={party.id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 3,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          mb: 2
                        }}
                      >
                        <Typography variant="h6" component="h3" fontWeight="bold">
                          {party.title}
                        </Typography>
                        <Chip 
                          label={t.host} 
                          color="primary" 
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <EventIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">
                          {new Date(party.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {party.location?.address || 'Location not specified'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">
                          {party.attendees ? party.attendees.length : 0} {t.attendees}
                        </Typography>
                      </Box>
                    </CardContent>
                    
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button 
                        component={Link} 
                        to={`/party/${party.id}`} 
                        color="primary"
                        endIcon={<ArrowForwardIcon />}
                        sx={{ ml: 'auto' }}
                      >
                        {t.viewDetails}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {t.noHostedParties}
                  </Typography>
                  <Button 
                    component={Link} 
                    to="/create-party" 
                    variant="contained" 
                    color="primary"
                    startIcon={<AddIcon />}
                    sx={{ mt: 2, borderRadius: 8 }}
                  >
                    {t.createParty}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Invited Parties */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography 
            variant="h5" 
            component="h2" 
            fontWeight="bold" 
            color="secondary.main" 
            gutterBottom
          >
            {t.invitedParties}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            {invitedParties.length > 0 ? (
              invitedParties.map(party => (
                <Grid item xs={12} sm={6} md={4} key={party.id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 3,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography variant="h6" component="h3" fontWeight="bold" gutterBottom>
                        {party.title}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <EventIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />
                        <Typography variant="body2" color="text.secondary">
                          {new Date(party.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {party.location?.address || 'Location not specified'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />
                        <Typography variant="body2" color="text.secondary">
                          {party.attendees ? party.attendees.length : 0} {t.attendees}
                        </Typography>
                      </Box>
                      
                      {party.rsvp_responses && party.rsvp_responses[user.id] ? (
                        <Chip 
                          label={party.rsvp_responses[user.id] === 'attending' ? t.attending : 
                                 party.rsvp_responses[user.id] === 'not_attending' ? t.notAttending : 
                                 t.maybe} 
                          color={party.rsvp_responses[user.id] === 'attending' ? 'success' : 
                                 party.rsvp_responses[user.id] === 'not_attending' ? 'error' : 
                                 'warning'} 
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      ) : (
                        <Chip 
                          label={t.rsvpNeeded} 
                          color="warning" 
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                    </CardContent>
                    
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button 
                        component={Link} 
                        to={`/party/${party.id}`} 
                        color="secondary"
                        endIcon={<ArrowForwardIcon />}
                        sx={{ ml: 'auto' }}
                      >
                        {t.viewDetails}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {t.noInvitedParties}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default Home; 